import React from "react"

// nextjs
import Link from "next/link"

// components
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Checkbox,
  Flex,
  Typography,
} from "antd"
import { LoginOutlined } from "@ant-design/icons"
import Logo from "components/Logo"

// firebase
import { Auth } from "services/firebase"
import { linkWithCredential, EmailAuthProvider } from "firebase/auth"
import { createUserWithEmailAndPassword } from "firebase/auth"

// store
import { useFunnel } from "store/funnel"
import { useAuth } from "store/auth"

// i18n
import { useTranslation } from "react-i18next"

const SignupModal = ({
  open,
  onCancel = () => null,
  onFinish = () => null,
}) => {
  const [signupForm] = Form.useForm()
  const [isLoading, setIsLoading] = React.useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const funnel = useFunnel()
  const { t } = useTranslation()
  const { user, setUser } = useAuth()

  return (
    <Modal
      data-testid="signup-modal"
      title={t("create_account")}
      footer={
        true ? null : (
          <Flex justify="end" gap={8}>
            <Typography.Text type="secondary" style={{ fontStyle: "italic" }}>
              provided by
            </Typography.Text>
            <Logo />
          </Flex>
        )
      }
      open={open}
      onCancel={onCancel}
    >
      {contextHolder}
      <Form
        form={signupForm}
        name="signup"
        layout="vertical"
        onFinish={async (values) => {
          try {
            if (values.password !== values.password2)
              throw Error(t("passwords_must_match"))

            setIsLoading(true)

            if (user && user.isAnonymous) {
              const credential = EmailAuthProvider.credential(
                values.email.trim(),
                values.password
              )
              await linkWithCredential(Auth.currentUser, credential)

              setUser((prev) => ({ ...prev, ...Auth.currentUser }))

              funnel.add("signup")
              onCancel()
              onFinish()
              signupForm.resetFields()
              return
            }

            await createUserWithEmailAndPassword(
              Auth,
              values.email.trim(),
              values.password
            )

            funnel.add("signup")

            onCancel()
            onFinish()
            signupForm.resetFields()
          } catch (e) {
            if (e.code === "auth/email-already-in-use") {
              messageApi.error(t("email_in_use"))
              return
            }

            if (e.code === "auth/weak-password") {
              messageApi.error(t("password_too_short"))
              return
            }

            messageApi.error(e.message)
            console.error(e)
          } finally {
            setIsLoading(false)
          }
        }}
      >
        <Typography.Paragraph type="secondary">
          {t("signup_info")}
        </Typography.Paragraph>

        <Form.Item
          label={t("email")}
          name="email"
          rules={[
            {
              required: true,
              message: t("please_enter_email2"),
            },
          ]}
        >
          <Input autoComplete="email" />
        </Form.Item>

        <Form.Item
          label={t("password")}
          name="password"
          rules={[
            {
              required: true,
              message: t("please_enter_password"),
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label={t("password_repeat")}
          name="password2"
          rules={[
            {
              required: true,
              message: t("please_repeat_password"),
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          valuePropName="checked"
          name="terms"
          rules={[
            {
              required: true,
              message: t("please_accept_terms"),
            },
          ]}
        >
          <Checkbox>
            {t("accept_terms")}
            <Link target="_blank" href="/terms">
              {t("terms_link")}
            </Link>
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Button
            icon={<LoginOutlined />}
            type="primary"
            htmlType="submit"
            loading={isLoading}
          >
            {t("sign_up")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default SignupModal
